import {
    Box,
    Grid,
    Toolbar,
    Typography,
    CircularProgress,
    Button,
    Pagination,
    TextField,
    Card,
    CardContent,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Stack,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import {
    FilterAltOutlined,
    Visibility,
    CalendarMonthOutlined,
} from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";

const MonthlySummaryReport = () => {
    const token = localStorage.getItem("token");
    const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

    const [selectedMonth, setSelectedMonth] = useState(
        new Date().toISOString().slice(0, 7)
    );

    const [loading, setLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [monthReportList, setMonthReportList] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [searchText, setSearchText] = useState("");

    const itemsPerPage = 20;
    const [currentPage, setCurrentPage] = useState(1);

    const formatCurrency = (amount = 0) => {
        return `₹${Number(amount || 0).toFixed(2)}`;
    };

    const handleApiError = (error) => {
        let errorMessage = "Something went wrong";

        if (error?.response?.status === 406) {
            errorMessage =
                error?.response?.data?.error?.message ||
                error?.response?.data?.error ||
                errorMessage;
        } else if (error?.response?.status === 412) {
            errorMessage = error?.response?.data?.data?.message || errorMessage;
        } else if (error?.response?.status === 500) {
            errorMessage = error?.response?.data?.error || errorMessage;
        }

        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
        });
    };

    const getMonthReport = async () => {
        if (!selectedMonth || !outletData?._id) return;

        setLoading(true);
        setClicked(true);

        try {
            const [year, month] = selectedMonth.split("-");

            const response = await axios.get(
                `${API.BASE_URL}order/monthlyOrderSummary?outletId=${outletData?._id}&year=${year}&month=${Number(
                    month
                )}&page=1&limit=50`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response?.data?.status === true) {
                setMonthReportList(response?.data?.data?.reportData || []);
                setCurrentPage(1);

                toast.success("Monthly report fetched successfully", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            handleApiError(error);
            setMonthReportList([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredRows = useMemo(() => {
        const rows = Array.isArray(monthReportList) ? monthReportList : [];
        const q = searchText.trim().toLowerCase();

        if (!q) return rows;

        return rows.filter((row) => {
            return (
                String(row?.date || "").toLowerCase().includes(q) ||
                String(row?.invoiceStartNo || "").toLowerCase().includes(q) ||
                String(row?.invoiceEndNo || "").toLowerCase().includes(q)
            );
        });
    }, [monthReportList, searchText]);

    const summary = useMemo(() => {
        return filteredRows.reduce(
            (acc, item) => {
                acc.totalOrders += Number(item?.numberOfInvoices || 0);
                acc.cancelledOrders += Number(item?.cancelledOrderCount || 0);
                acc.totalDiscount += Number(item?.totalDiscount || 0);
                acc.cancelledAmount += Number(item?.cancelledOrderAmount || 0);
                acc.totalTaxable += Number(item?.totalTaxable || 0);
                acc.totalTax += Number(item?.totalTax || item?.totalTaxAmount || 0);
                acc.grandTotal += Number(item?.grandTotal || 0);
                return acc;
            },
            {
                totalOrders: 0,
                cancelledOrders: 0,
                totalDiscount: 0,
                cancelledAmount: 0,
                totalTaxable: 0,
                totalTax: 0,
                grandTotal: 0,
            }
        );
    }, [filteredRows]);

    const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;

    const paginatedRows = filteredRows.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const getTaxBreakup = (report) => {
        if (!Array.isArray(report?.taxDetails)) return [];

        const taxMap = {};

        report.taxDetails.forEach((tax) => {
            const percent = tax?.taxPercent || 0;

            if (!taxMap[percent]) {
                taxMap[percent] = {
                    taxPercent: percent,
                    taxable: 0,
                    CGST: 0,
                    SGST: 0,
                };
            }

            taxMap[percent].taxable += Number(tax?.taxable || 0);
            taxMap[percent].CGST += Number(tax?.CGST || 0);
            taxMap[percent].SGST += Number(tax?.SGST || 0);
        });

        return Object.values(taxMap);
    };

    return (
        <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
            <Toolbar />

            <Grid container justifyContent="center" sx={{ pb: 6 }}>
                <Grid item xs={12} md={11}>
                    <Card
                        elevation={0}
                        sx={{
                            mt: 4,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                        }}
                    >
                        <CardContent sx={{ py: 3 }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Box>
                                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                                        Monthly Outlet Summary
                                    </Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                        View day-wise monthly sales summary for your outlet.
                                    </Typography>
                                </Box>

                                <Chip
                                    icon={<FilterAltOutlined />}
                                    label={`Records: ${filteredRows?.length || 0}`}
                                    sx={{ fontWeight: 700, borderRadius: 2 }}
                                    variant="outlined"
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Select Month
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        type="month"
                                        value={selectedMonth}
                                        onChange={(e) => {
                                            setSelectedMonth(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarMonthOutlined />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                height: 52,
                                                background: "#fff",
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Search
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Search by date / invoice no"
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRounded />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                height: 52,
                                                background: "#fff",
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Action
                                    </Typography>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disabled={loading}
                                        onClick={getMonthReport}
                                        sx={{
                                            height: 52,
                                            borderRadius: 2,
                                            fontWeight: 800,
                                            textTransform: "none",
                                            backgroundColor: "#003049",
                                            "&:hover": { backgroundColor: "#00263a" },
                                        }}
                                    >
                                        {loading ? "Loading..." : "Get"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            overflow: "hidden",
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                    <CircularProgress />
                                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                                        Fetching Monthly Summary...
                                    </Typography>
                                </Box>
                            ) : !clicked ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                                        Select month to view report
                                    </Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                        Choose a month and click “Get” to load outlet summary.
                                    </Typography>
                                </Box>
                            ) : filteredRows?.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                                        No report found
                                    </Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                        No data available for selected month.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0" }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                <Chip
                                                    icon={<FilterAltOutlined />}
                                                    label={`Rows: ${filteredRows?.length || 0}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />

                                                <Chip
                                                    label={`Orders: ${summary.totalOrders}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />

                                                <Chip
                                                    label={`Cancelled: ${summary.cancelledOrders}`}
                                                    sx={{
                                                        fontWeight: 800,
                                                        borderRadius: 2,
                                                        color: "#b91c1c",
                                                        backgroundColor: "#fee2e2",
                                                        border: "1px solid #fca5a5",
                                                    }}
                                                />

                                                <Chip
                                                    label={`Tax: ${formatCurrency(summary.totalTax)}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />

                                                <Chip
                                                    label={`Taxable: ${formatCurrency(summary.totalTaxable)}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />

                                                <Chip
                                                    label={`Discount: ${formatCurrency(summary.totalDiscount)}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />
                                            </Stack>

                                            <Chip
                                                label={`Grand Total: ${formatCurrency(summary.grandTotal)}`}
                                                sx={{
                                                    fontWeight: 900,
                                                    borderRadius: 2,
                                                    backgroundColor: "#003049",
                                                    color: "#fff",
                                                    "& .MuiChip-label": { color: "#fff" },
                                                }}
                                            />
                                        </Stack>
                                    </Box>

                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            borderRadius: 0,
                                            maxHeight: "70vh",
                                            overflow: "auto",
                                        }}
                                    >
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {["Date", "Orders", "Cancelled", "Taxable", "Tax", "Grand Total", "Detail"].map((h) => (
                                                        <TableCell
                                                            key={h}
                                                            align={h === "Date" ? "left" : "center"}
                                                            sx={{
                                                                fontWeight: 900,
                                                                color: "#0f172a",
                                                                background: "#f1f5f9",
                                                                borderBottom: "1px solid #e2e8f0",
                                                                whiteSpace: "nowrap",
                                                                position: "sticky",
                                                                top: 0,
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            {h}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {paginatedRows.map((report, index) => (
                                                    <TableRow
                                                        key={`${report?.date}-${index}`}
                                                        hover
                                                        sx={{
                                                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                                            "& td": { borderBottom: "1px solid #f1f5f9" },
                                                        }}
                                                    >
                                                        <TableCell align="left">
                                                            <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                                                                {report?.date || "N/A"}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                                                Invoice: {report?.invoiceStartNo || "-"} to {report?.invoiceEndNo || "-"}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                            {report?.numberOfInvoices || 0}
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <Chip
                                                                size="small"
                                                                label={report?.cancelledOrderCount || 0}
                                                                sx={{
                                                                    height: 24,
                                                                    fontWeight: 800,
                                                                    borderRadius: 2,
                                                                    color:
                                                                        report?.cancelledOrderCount > 0 ? "#b91c1c" : "#334155",
                                                                    backgroundColor:
                                                                        report?.cancelledOrderCount > 0 ? "#fee2e2" : "#e2e8f0",
                                                                }}
                                                            />
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                            {formatCurrency(report?.totalTaxable)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                            {formatCurrency(report?.totalTax || report?.totalTaxAmount || 0)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                                            {formatCurrency(report?.grandTotal)}
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <IconButton onClick={() => setSelectedDay(report)}>
                                                                <Visibility />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Divider />

                                    <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage}
                                            onChange={(event, page) => setCurrentPage(page)}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog
                open={Boolean(selectedDay)}
                onClose={() => setSelectedDay(null)}
                maxWidth="sm"
                fullWidth
            >
                {/* HEADER */}
                <DialogTitle
                    sx={{
                        textAlign: "center",
                        py: 1,
                        borderBottom: "1px solid #e2e8f0",
                    }}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#003049" }}>
                        Daily Sales Summary
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {selectedDay?.date || "N/A"}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 1.5 }}>
                    {selectedDay ? (
                        <Grid container spacing={1}>

                            {/* TOP CARDS - ONE ROW */}
                            <Grid item xs={12}>
                                <Grid container spacing={1}>
                                    {[
                                        {
                                            label: "Orders",
                                            value: selectedDay?.numberOfInvoices || 0,
                                            bg: "#003049",
                                            color: "#fff",
                                        },
                                        {
                                            label: "Cancelled",
                                            value: selectedDay?.cancelledOrderCount || 0,
                                            bg: "#fee2e2",
                                            color: "#b91c1c",
                                        },
                                        {
                                            label: "Tax",
                                            value: formatCurrency(
                                                selectedDay?.totalTax || selectedDay?.totalTaxAmount || 0
                                            ),
                                            bg: "#f1f5f9",
                                            color: "#0f172a",
                                        },
                                        {
                                            label: "Total",
                                            value: formatCurrency(selectedDay?.grandTotal),
                                            bg: "#dcfce7",
                                            color: "#166534",
                                        },
                                    ].map((item, i) => (
                                        <Grid item xs={3} key={i}>
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    background: item.bg,
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 10, color: item.color }}>
                                                    {item.label}
                                                </Typography>
                                                <Typography
                                                    sx={{ fontSize: 13, fontWeight: 800, color: item.color }}
                                                >
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* ROW 1 */}
                            <Grid item xs={12}>
                                <Grid container spacing={1}>
                                    {[
                                        ["Invoice Start", selectedDay?.invoiceStartNo],
                                        ["Invoice End", selectedDay?.invoiceEndNo],
                                        ["Orders", selectedDay?.numberOfInvoices],
                                    ].map(([label, value], i) => (
                                        <Grid item xs={4} key={i}>
                                            <Box sx={{ textAlign: "center" }}>
                                                <Typography sx={{ fontSize: 10, color: "#64748b" }}>
                                                    {label}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                    {value || "-"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* ROW 2 */}
                            <Grid item xs={12}>
                                <Grid container spacing={1}>
                                    {[
                                        [
                                            "Cancelled Amt",
                                            formatCurrency(selectedDay?.cancelledOrderAmount),
                                        ],
                                        ["Discount", formatCurrency(selectedDay?.totalDiscount)],
                                        ["Taxable", formatCurrency(selectedDay?.totalTaxable)],
                                    ].map(([label, value], i) => (
                                        <Grid item xs={4} key={i}>
                                            <Box sx={{ textAlign: "center" }}>
                                                <Typography sx={{ fontSize: 10, color: "#64748b" }}>
                                                    {label}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                    {value || "-"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* TOTAL */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        py: 1,
                                        borderRadius: 1,
                                        background: "#f8fafc",
                                        border: "1px dashed #cbd5f5",
                                    }}
                                >
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                        Net Payable
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: 16, fontWeight: 900, color: "#003049" }}
                                    >
                                        {formatCurrency(selectedDay?.grandTotal)}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* TAX TABLE */}
                            <Grid item xs={12}>
                                <Box sx={{ mt: 0.5 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            mb: 0.5,
                                            fontSize: 13,
                                            textAlign: "center",
                                            color: "#003049",
                                        }}
                                    >
                                        Tax Overview
                                    </Typography>

                                    {getTaxBreakup(selectedDay).length > 0 ? (
                                        <Box
                                            component="table"
                                            sx={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                "& th, & td": {
                                                    padding: "5px",
                                                    border: "1px solid #e2e8f0",
                                                    fontSize: 10,
                                                },
                                                "& th": {
                                                    background: "#f1f5f9",
                                                },
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Tax %</th>
                                                    <th style={{ textAlign: "right" }}>Taxable</th>
                                                    <th style={{ textAlign: "right" }}>CGST</th>
                                                    <th style={{ textAlign: "right" }}>SGST</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getTaxBreakup(selectedDay).map((tax, index) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: "center" }}>
                                                            {tax.taxPercent}%
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            {formatCurrency(tax.taxable)}
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            {formatCurrency(tax.CGST)}
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            {formatCurrency(tax.SGST)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Box>
                                    ) : (
                                        <Typography sx={{ textAlign: "center", fontSize: 11 }}>
                                            No tax details available.
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography>No details available</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default MonthlySummaryReport;